'use client';

import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';

import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Check,
  Heart,
  Package,
  Shield,
  Sparkles,
  Truck,
  ChevronLeft,
  RotateCcw
} from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { ProductCard } from '@/components/ProductCard';
import { useProduct } from '@/hooks/useProducts';
import { useCart } from '@/providers/CartProvider';
import { useAuth } from '@/providers/AuthProvider';
import { formatDate } from '@/lib/utils/format';
import { useNotification } from '@/providers/NotificationProvider';
import { useRouter } from 'next/navigation';
import { AXIOS_CONFIG } from '@/lib/Apis/wrapper';
import { useWishlist } from '@/providers/WishlistProvider';
import { ProductsApi, ProductVariant, ProductVariantInventoryStatusEnum } from '@/lib/Apis';
import { getDeviceId } from '@/lib/utils/deviceId';
import { getPersonalizationSessionStartedAt } from '@/lib/utils/personalizationSession';
import { useBasePath } from '@/providers/BasePathProvider';
import { Category } from '@/lib/types';
// import { useProductReviews } from '@/hooks/useReviews';
// import { ProductReviewsSection } from '@/components/ProductReviewsSection';
import { NotifyWhenInStockButton } from '@/components/NotifyWhenInStockButton';
import { ProductCardSkeleton } from '@/components/ui/Skeleton';

const safeFormatDate = (date?: Date | string, format: 'long' | 'short' = 'long'): string => {
  if (!date) return 'N/A';
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return formatDate(dateObj, format);
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'N/A';
  }
};

interface ProductDetailScreenProps {
  productId: string;
}

export function ProductDetailScreen({ productId }: ProductDetailScreenProps) {
  // Hooks and state at the top level
  const router = useRouter();
  const { buildPath } = useBasePath();
  const { product: productData, isLoading } = useProduct(productId);
  const { addToCart } = useCart();
  const { isAuthenticated } = useAuth();
  const notification = useNotification();
  // Reviews are temporarily hidden.
  // const { reviews, isLoading: reviewsLoading } = useProductReviews(productId);

  // State declarations
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [isFavorited, setIsFavorited] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState<any[]>([]);
  const [isLoadingRelated, setIsLoadingRelated] = useState(false);
  const [initialProductData, setInitialProductData] = useState<any>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState<
    'details' | 'ingredients' | 'specifications'
    // | 'reviews'
  >('details');
  const [hasUserSelectedVariant, setHasUserSelectedVariant] = useState(false);


  useEffect(() => {
    setActiveImageIndex(0);
  }, [selectedVariant]);

  const product = useMemo(() => {
    if (initialProductData && !productData) {
      return initialProductData;
    }

    if (!productData) return null;

    if (productData.variants?.length && selectedVariant) {
      return {
        ...productData,
        price: selectedVariant.finalPrice,
        inStock: selectedVariant.isActive,
        sku: selectedVariant.sku || productData.sku,
        variantId: selectedVariant._id,
        variantName: selectedVariant.name
      };
    }

    return productData;
  }, [productData, selectedVariant, initialProductData]);

  // Reviews are temporarily hidden.
  // const reviewStats = useMemo(() => {
  //   if (!reviews || reviews.length === 0) {
  //     return {
  //       averageRating: product?.rating || 0,
  //       reviewCount: 0,
  //     };
  //   }
  //
  //   const totalRating = reviews.reduce((sum, review) => sum + (review.rating || 0), 0);
  //   const averageRating = totalRating / reviews.length;
  //
  //   return {
  //     averageRating: Math.round(averageRating * 10) / 10,
  //     reviewCount: reviews.length,
  //   };
  // }, [reviews, product?.rating]);

  const getVariantImages = () => {
    if (hasUserSelectedVariant && selectedVariant?.media?.length) {
      return selectedVariant.media.map((media: any) => ({
        src: media.file,
        width: 800,
        height: 1000,
        alt: product?.name || 'Product image',
        ...media,
        url: media.file,
      }));
    }

    // Show product images by default (before user selects a variant)
    if (product?.media?.length) {
      return product.media.map((media: any) => ({
        src: media.file,
        width: 800,
        height: 1000,
        alt: product?.name || 'Product image',
        ...media,
        url: media.file,
      }));
    }

    return [];
  };

  const currentVariant = selectedVariant || product?.variants?.[0] || product;
  const variantImages = getVariantImages();

  // Use only the selected variant's prices for discount calculation
  const variantPrice = selectedVariant?.finalPrice || currentVariant?.finalPrice || product?.price || 0;
  const variantComparePrice = selectedVariant?.retailPrice ||
    (currentVariant && 'retailPrice' in currentVariant ? currentVariant.retailPrice : null) ||
    product?.compareAtPrice;

  // Only show discount if we have both prices and the compare price is higher
  const discount = variantComparePrice && variantPrice && variantComparePrice > variantPrice
    ? Math.round(((variantComparePrice - variantPrice) / variantComparePrice) * 100)
    : 0;
  const lastUpdatedLabel = safeFormatDate(
    (currentVariant && 'updatedAt' in currentVariant && currentVariant.updatedAt)
      ? currentVariant.updatedAt
      : product?.updatedAt
  );
  const variantSku = currentVariant?.sku || product?.sku || 'N/A';
  // const variantInStock = currentVariant && 'inStock' in currentVariant
  //   ? currentVariant.inStock
  //   : product?.inStock;

  const variantInStock = currentVariant?.inventoryCount > 0;


  useEffect(() => {
    if (typeof window === 'undefined') return;

    const searchParams = new URLSearchParams(window.location.search);
    const productParam = searchParams.get('product');

    if (productParam) {
      try {
        const decodedProduct = JSON.parse(decodeURIComponent(productParam));
        setInitialProductData(decodedProduct);
      } catch (error) {
        console.error('Failed to parse product data from URL', error);
      }
    }
  }, []);



  useEffect(() => {
    if (product?.variants?.length > 0) {
      const requestedVariantId = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('variant')
        : null;
      const requestedVariant = requestedVariantId
        ? product.variants.find((variant: any) => String(variant._id || variant.id) === requestedVariantId)
        : undefined;

      // Prefer first in-stock variant
      const inStockVariant = product.variants.find((v: any) => (v.inventoryCount ?? 0) > 0);
      const newVariant = requestedVariant || inStockVariant || product.variants[0];
      setHasUserSelectedVariant(!!requestedVariant);
      setSelectedVariant(newVariant);
    }
  }, [product?.variants]);

  const hasFetchedRelated = React.useRef<string | null>(null);
  useEffect(() => {
    if (!product?._id || hasFetchedRelated.current === product._id) return;
    hasFetchedRelated.current = product._id;

    const fetchRelated = async () => {
      setIsLoadingRelated(true);
      try {
        const response = await new ProductsApi(AXIOS_CONFIG).getRelatedProducts(product._id, 4);
        const lightProducts = response.data || [];

        // Fetch full details for each related product to get description, variants, etc.
        const fullProducts = await Promise.all(
          lightProducts.map(async (lightProduct: any) => {
            try {
              const fullResponse = await new ProductsApi(AXIOS_CONFIG).getSingleProduct(lightProduct._id || lightProduct.id);
              return fullResponse.data;
            } catch (error) {
              console.error(`Failed to fetch full details for product ${lightProduct._id}`, error);
              return lightProduct; // Fallback to light data
            }
          })
        );

        setRelatedProducts(fullProducts);
      } catch (error: any) {
        console.error('Failed to fetch related products', error);
      } finally {
        setIsLoadingRelated(false);
      }
    };
    fetchRelated();
  }, [product?._id]);

  // Track product view for personalized recommendations
  const hasTrackedView = React.useRef<string | null>(null);
  useEffect(() => {
    if (!productId || hasTrackedView.current === productId) return;
    hasTrackedView.current = productId;

    const trackView = async () => {
      try {
        const deviceId = getDeviceId();
        const personalizationSessionStartedAt = getPersonalizationSessionStartedAt();
        await new ProductsApi(AXIOS_CONFIG).trackProductView(productId, deviceId, {
          headers: personalizationSessionStartedAt
            ? { 'x-personalization-session-started-at': personalizationSessionStartedAt }
            : undefined,
        });
      } catch (error) {
        // Fire-and-forget - don't block UX on tracking errors
        console.debug('Failed to track product view', error);
      }
    };
    trackView();
  }, [productId]);

  const handleVariantSelect = async (variant: ProductVariant) => {
    setSelectedVariant(variant);
    setHasUserSelectedVariant(true);
    // Reset to first image of the selected variant
    setActiveImageIndex(0);
  };

  const handleAddToCart = async () => {
    if (!product || !selectedVariant) return;

    setIsAddingToCart(true);
    try {
      await addToCart(
        product._id,
        quantity,
        selectedVariant._id
      );
    } catch (error) {
      console.error('Failed to add to cart', error);
    } finally {
      setIsAddingToCart(false);
    }
  };

  const { addToWishlist, removeFromWishlist, isInWishlist } = useWishlist();

  // Initialize isFavorited based on whether the product is in the wishlist
  useEffect(() => {
    if (product) {
      setIsFavorited(isInWishlist(product._id));
    }
  }, [product, isInWishlist]);

  const handleToggleFavorite = async () => {
    if (!product) return;
    if (!isAuthenticated) {
      notification.error(
        'Sign-in required',
        'Please sign in to add items to your favorites.'
      );
      router.push(buildPath(`/login?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`));
      return;
    }

    try {
      if (isFavorited) {
        await removeFromWishlist(product._id);
      } else {
        await addToWishlist(product);
      }
      // Update the local state after successful API call
      setIsFavorited(!isFavorited);
    } catch (error) {
      console.error('Error updating wishlist:', error);
      notification.error(
        'Could not update favorites',
        'We could not update your favorites. Please try again.'
      );
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <section className="py-6 sm:py-12">
          <div className="relative pb-10 sm:pb-20 pt-4 sm:pt-8">
            <div className="container mx-auto px-4">
              {/* Top grid — matches lg:grid-cols-[50fr_50fr] */}
              <div className="grid gap-6 sm:gap-10 lg:grid-cols-[minmax(0,50fr)_minmax(0,50fr)]">

                {/* Left: main image + thumbnails */}
                <div className="space-y-6 sm:space-y-10">
                  <div className="h-[280px] sm:h-[420px] md:h-[560px] animate-pulse rounded-2xl sm:rounded-3xl bg-slate-200" />
                  <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1 sm:gap-2">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="aspect-square animate-pulse rounded-lg bg-slate-200" />
                    ))}
                  </div>
                </div>

                {/* Right: product info */}
                <div className="space-y-4">
                  {/* Brand pill */}
                  <div className="h-6 w-20 animate-pulse rounded-full bg-slate-200" />
                  {/* Title */}
                  <div className="space-y-2">
                    <div className="h-8 sm:h-10 w-3/4 animate-pulse rounded-full bg-slate-200" />
                    <div className="h-8 sm:h-10 w-1/2 animate-pulse rounded-full bg-slate-200" />
                  </div>
                  {/* Variant name */}
                  <div className="h-4 w-1/3 animate-pulse rounded-full bg-slate-200" />
                  {/* Price row */}
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-24 animate-pulse rounded-full bg-slate-200" />
                    <div className="h-6 w-16 animate-pulse rounded-full bg-slate-200" />
                  </div>
                  {/* Divider */}
                  <div className="h-px w-full bg-slate-200" />
                  {/* Variant selector label */}
                  <div className="h-4 w-28 animate-pulse rounded-full bg-slate-200" />
                  {/* Variant cards */}
                  <div className="flex flex-wrap gap-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                      <div key={i} className="h-14 w-36 animate-pulse rounded-xl bg-slate-200" />
                    ))}
                  </div>
                  {/* Stock indicator */}
                  <div className="h-4 w-40 animate-pulse rounded-full bg-slate-200" />
                  {/* Action row: quantity + add to cart + heart */}
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-28 animate-pulse rounded-full bg-slate-200" />
                    <div className="h-12 flex-1 animate-pulse rounded-full bg-slate-200" />
                    <div className="h-12 w-12 animate-pulse rounded-full bg-slate-200" />
                  </div>
                </div>
              </div>

              {/* Tabs skeleton */}
              <div className="mt-10 sm:mt-16 mb-10 sm:mb-16">
                <div className="flex items-center gap-2 border-b border-slate-100 pb-1 mb-6 sm:mb-8">
                  {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className={`h-9 animate-pulse rounded-full bg-slate-200 ${i === 0 ? 'w-36' : i === 1 ? 'w-32' : 'w-28'}`} />
                  ))}
                </div>
                <div className="rounded-2xl sm:rounded-[24px] bg-white p-4 sm:p-8 space-y-4">
                  <div className="h-5 w-40 animate-pulse rounded-full bg-slate-200" />
                  <div className="h-4 w-full animate-pulse rounded-full bg-slate-200" />
                  <div className="h-4 w-5/6 animate-pulse rounded-full bg-slate-200" />
                  <div className="h-4 w-4/6 animate-pulse rounded-full bg-slate-200" />
                  <div className="h-4 w-full animate-pulse rounded-full bg-slate-200" />
                  <div className="h-4 w-3/4 animate-pulse rounded-full bg-slate-200" />
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (!product || product.isActive === false) {
    return (
      <div className="min-h-screen bg-transparent max-w-fit mx-auto">
        <div className="container mx-auto px-4 py-16">
          <div className="rounded-3xl bg-white p-10 text-center shadow-xs">
            <Sparkles className="mx-auto h-10 w-10 text-hprimary-500" />
            <h1 className="mt-6 text-2xl font-semibold text-gray-900">Product not found</h1>
            <p className="mt-2 text-gray-600">
              It may have been removed or is temporarily unavailable. Discover other pharmacy
              essentials in our catalogue.
            </p>
            <div className="mt-6">
              <Link href="/shop" className="inline-block">
                <Button>Browse products</Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }




  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Breadcrumb */}
      {/* <div className="bg-gray-50 border-b border-gray-200 py-3 sm:py-4">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-8 md:px-12">
          <button
            onClick={() => router.push(buildPath('/shop'))}
            className="flex items-center gap-2 text-[13px] text-hmuted hover:text-hprimary transition-colors"
          >
            <ChevronLeft className="size-4" />
            Back to Shop
          </button>
        </div>
      </div> */}

      <section className="py-6 sm:py-12">
        <div className="relative pb-10 sm:pb-20 pt-4 sm:pt-8">
          <div className="container mx-auto px-4">
            <div className="grid gap-6 sm:gap-10 lg:grid-cols-[minmax(0,50fr)_minmax(0,50fr)]">
              <div className="space-y-6 sm:space-y-10">
                <section className="rounded-3xl ">
                  <div className="space-y-6">
                    <motion.div
                      key={selectedVariant?._id || 'default'}
                      initial={{ opacity: 0.4, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.35 }}
                      className="relative overflow-hidden rounded-2xl sm:rounded-3xl bg-white h-[280px] sm:h-[420px] md:h-[560px]"
                    >
                      {variantImages.length > 0 && variantImages[activeImageIndex] ? (
                        <Image
                          src={variantImages[activeImageIndex].src || variantImages[activeImageIndex].url || variantImages[activeImageIndex].file}
                          alt={currentVariant.name || product.name}
                          fill
                          priority
                          sizes="(max-width: 1024px) 100vw, 800px"
                          className="object-contain"
                        />
                      ) : product?.media?.[0]?.file ? (
                        <Image
                          src={product.media[0].file}
                          alt={product.name}
                          fill
                          priority
                          sizes="(max-width: 1024px) 100vw, 800px"
                          className="object-contain"
                        />
                      ) : null}

                      <div className="absolute top-3 left-3 sm:top-6 sm:left-6 flex flex-col gap-2 sm:gap-3">
                        {discount > 0 && (
                          <div className="bg-hsecondary text-white rounded-full px-4 py-2">
                            <span className="font-bold text-[12px] uppercase tracking-wide">
                              Save {discount}%
                            </span>
                          </div>
                        )}
                        {/* {product.bestseller && (
                          <div className="bg-hsecondary text-white rounded-full px-4 py-2">
                            <span className="font-semibold text-[11px] uppercase tracking-wide">
                              Bestseller
                            </span>
                          </div>
                        )}
                        {product.newArrival && (
                          <div className="bg-hprimary text-white rounded-full px-4 py-2">
                            <span className="font-semibold text-[11px] uppercase tracking-wide">
                              New Arrival
                            </span>
                          </div>
                        )} */}
                      </div>
                    </motion.div>

                    {/* Variant Photos Below Main Photo */}
                    {variantImages.length > 0 && (
                      <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-1 sm:gap-2">
                        {variantImages.map((image: { src: string; alt?: string }, index: number) => (
                          <button
                            key={image.src + index}
                            type="button"
                            onClick={() => setActiveImageIndex(index)}
                            className={`relative aspect-square overflow-hidden rounded-lg border-2 transition-all ${activeImageIndex === index
                              ? 'border-hprimary-50 ring-2 ring-hprimary-80 ring-offset-2 shadow-md'
                              : 'border-slate-200 hover:border-hprimary-50'
                              }`}
                          >
                            <Image
                              src={image?.src}
                              alt={image.alt || `Product image ${index + 1}`}
                              className="h-full w-full object-cover object-center"
                              width={80}
                              height={80}
                              unoptimized={true}
                            />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </section>
              </div>


              <aside className="lg:sticky lg:top-24 min-w-0">
                <div>
                  {/* Brand */}
                  {product.brand && (
                    <p className="text-[11px] text-hmuted uppercase tracking-widest font-medium mb-3 break-words border rounded-full border-hmuted/30 inline-block px-2 py-1">
                      {product.brand}
                    </p>
                  )}

                  {/* Title */}
                  <h1 className="text-xl sm:text-4xl font-medium text-hsecondary tracking-[-0.5px] sm:tracking-[-1.5px] mb-4 break-words max-w-full sm:max-w-sm">
                    {product.name}
                  </h1>

                  {/* Selected variant */}
                  {selectedVariant && (
                    <p className="text-sm text-hmuted mb-4">
                      {selectedVariant.name}
                    </p>
                  )}

                  {/* Price */}
                  <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
                    <span className="text-2xl text-hsecondary font-medium">
                      ${variantPrice.toFixed(2)}
                    </span>
                    {variantComparePrice && variantComparePrice > variantPrice && (
                      <span className="text-[18px] text-hmuted line-through">
                        ${variantComparePrice.toFixed(2)}
                      </span>
                    )}
                  </div>
                  <div className="border-b border-gray-200 mb-6" />

                  {/* Variant Selector with Images */}
                  {product?.variants && product.variants.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-semibold text-[14px] text-hsecondary mb-3">
                        Select Variant
                      </h3>
                      <div className="flex flex-wrap gap-3">
                        {product.variants.map((variant: ProductVariant) => {
                          const isSelected = selectedVariant?._id === variant._id;
                          const variantImage = variant.media?.[0]?.file
                            || product.media?.[0]?.file
                            || product.images?.[0]
                            || PLACEHOLDER_IMAGE_SRC;
                          return (
                            <button
                              key={variant._id}
                              type="button"
                              onClick={() => handleVariantSelect(variant)}
                              className={`flex items-start gap-3 px-4 py-2.5 rounded-xl border-2 transition-all ${isSelected
                                ? 'border-hprimary bg-hprimary-5'
                                : 'border-gray-200 hover:border-hprimary-50'
                                }`}
                            >
                              <div className={`relative h-12 w-12 shrink-0 overflow-hidden rounded-full border-2 ${isSelected ? 'border-hprimary' : 'border-slate-200'}`}>
                                <Image
                                  src={variantImage}
                                  alt={variant.name || 'Variant image'}
                                  fill
                                  className="object-cover"
                                  sizes="48px"
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-start text-sm font-medium break-words ${isSelected ? 'text-hsecondary' : 'text-slate-900'}`}>
                                  {variant.name}
                                </p>
                                {variant.sku && (
                                  <p className="text-start text-xs text-slate-500 mt-0.5 break-all">SKU: {variant.sku}</p>
                                )}
                              </div>
                              {isSelected && (
                                <Check className="h-5 w-5 text-hsecondary shrink-0" />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Stock level indicator */}
                  {product.showStock && selectedVariant && (
                    <div className="mb-4">
                      {selectedVariant.inventoryStatus === ProductVariantInventoryStatusEnum.OUTOFSTOCK ? (
                        <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-gray-500">
                          <span className="w-2 h-2 rounded-full bg-gray-400 shrink-0" />
                          Out of Stock
                        </span>
                      ) : selectedVariant.inventoryStatus === ProductVariantInventoryStatusEnum.LOWSTOCK ? (
                        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-amber-600">
                          <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                          Only {selectedVariant.inventoryCount} left in stock — order soon
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-green-700">
                          <span className="w-2 h-2 rounded-full bg-green-500 shrink-0" />
                          {selectedVariant.inventoryCount} in stock
                        </span>
                      )}
                    </div>
                  )}

                  {/* Quantity + Add to Cart + Heart - single row */}
                  <div className="flex items-center gap-2 sm:gap-3 mb-2">
                    {/* Quantity picker */}
                    <div className="flex items-center gap-1.5 sm:gap-3 border-2 border-gray-200 rounded-full px-2.5 sm:px-5 py-2.5 sm:py-3 shrink-0">
                      <button
                        type="button"
                        onClick={() => setQuantity((current) => Math.max(1, current - 1))}
                        className="font-bold text-[16px] text-hsecondary hover:text-hprimary transition-colors leading-none"
                        aria-label="Decrease quantity"
                      >
                        −
                      </button>
                      <span className="font-semibold text-[15px] text-hsecondary min-w-5 text-center">
                        {quantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const maxStock = selectedVariant?.inventoryCount || product.summary?.totalInventory || 999;
                          if (quantity >= maxStock) {
                            notification.error(
                              'Maximum quantity reached',
                              product.showStock
                                ? `Sorry, only ${maxStock} units of this item are available.`
                                : 'Sorry, you\'ve reached the maximum available quantity.'
                            );
                            return;
                          }
                          setQuantity(quantity + 1);
                        }}
                        className="font-bold text-[16px] text-hsecondary hover:text-hprimary transition-colors leading-none"
                        aria-label="Increase quantity"
                      >
                        +
                      </button>
                    </div>

                    {/* Add to Cart / Notify */}
                    <div className="min-w-0 flex-1">
                      {selectedVariant?.inventoryStatus === ProductVariantInventoryStatusEnum.OUTOFSTOCK ? (
                        <NotifyWhenInStockButton
                          productId={productId}
                          variantId={selectedVariant._id || selectedVariant.id}
                          size="sm"
                          className="whitespace-nowrap"
                        />
                      ) : (
                        <button
                          className="w-full min-w-0 font-medium text-[11px] min-[380px]:text-[12px] sm:text-[14px] px-2.5 sm:px-6 py-3 sm:py-3.5 rounded-full transition-all duration-300 flex items-center justify-center gap-2 bg-hsecondary text-white hover:bg-hsecondary/80 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                          onClick={handleAddToCart}
                          disabled={!selectedVariant || isAddingToCart}
                        >
                          {isAddingToCart ? (
                            <>
                              <svg className="animate-spin h-4 w-4 shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                              <span className="hidden min-[360px]:inline">Adding...</span>
                            </>
                          ) : !selectedVariant ? (
                            <>
                              <span className="sm:hidden">Select</span>
                              <span className="hidden sm:inline">Select a variant</span>
                            </>
                          ) : (
                            <>
                              <span className="sm:hidden">Add to cart</span>
                              <span className="hidden sm:inline">Add to Cart{variantPrice ? ` · $${variantPrice.toFixed(2)}` : ''}</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>

                    {/* Heart */}
                    <button
                      className="shrink-0 p-3 sm:p-3.5 rounded-full border-2 border-gray-200 hover:bg-gray-50 transition-all flex items-center justify-center"
                      onClick={handleToggleFavorite}
                      aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Heart className={`h-4 w-4 ${isFavorited ? 'fill-red-500 text-red-500' : 'text-hmuted'}`} />
                    </button>
                  </div>

                  {product.showStock && quantity >= (selectedVariant?.inventoryCount || product.summary?.totalInventory || 999) && (
                    <p className="text-[11px] text-hprimary font-medium mb-4">
                      Maximum available reached
                    </p>
                  )}
                </div>
              </aside>
            </div>


            <div className="mb-10 sm:mb-16 mt-10">
              <div className="flex items-center mb-6 sm:mb-8 border-b border-hsecondary/10 overflow-x-auto scrollbar-hide">
                {([
                  { key: 'details', label: 'Product Details' },
                  ...(product.ingredients?.length > 0 ? [{ key: 'ingredients', label: 'Ingredients' }] : []),
                  { key: 'specifications', label: 'Specifications' },
                  // Reviews are temporarily hidden.
                  // { key: 'reviews', label: `Reviews (${reviewStats.reviewCount})` },
                ] as const).map(({ key, label }: any) => (
                  <button
                    key={key}
                    onClick={() => setActiveTab(key)}
                    className={`font-medium text-[12px] sm:text-[14px] px-3 sm:px-6 py-3 sm:py-4 transition-all whitespace-nowrap shrink-0 ${activeTab === key
                      ? 'text-hprimary border-b-2 border-hprimary -mb-0.5'
                      : 'text-hmuted hover:text-hprimary'
                      }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="rounded-2xl sm:rounded-[24px] p-4 sm:p-8">
                {activeTab === 'details' && (
                  <div>
                    <h3 className="font-semibold text-hsecondary mb-4">
                      Product Description
                    </h3>
                    <div
                      className="text-[14px] text-hmuted leading-[1.8] mb-4 max-w-full overflow-hidden break-words"
                      dangerouslySetInnerHTML={{ __html: product.description }}
                    />
                  </div>
                )}

                {activeTab === 'ingredients' && (
                  <div>
                    <h3 className="font-semibold text-hsecondary mb-4">
                      Ingredients
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {product.ingredients.map((ingredient: string, i: number) => (
                        <span
                          key={i}
                          className="text-[13px] text-hmuted bg-slate-50 border border-slate-200 rounded-full px-3 py-1"
                        >
                          {ingredient}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {activeTab === 'specifications' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-hsecondary mb-4">
                      Specifications
                    </h3>
                    {product.sku && (
                      <div className="flex flex-wrap justify-between gap-1 py-2 border-b border-gray-100 text-[14px]">
                        <span className="text-hmuted">SKU</span>
                        <span className="font-medium text-hsecondary">{product.sku}</span>
                      </div>
                    )}
                    {product.brand && (
                      <div className="flex flex-wrap justify-between gap-1 py-2 border-b border-gray-100 text-[14px]">
                        <span className="text-hmuted">Brand</span>
                        <span className="font-medium text-hsecondary">{product.brand}</span>
                      </div>
                    )}
                    {product.categoryIds?.[0]?.name && (
                      <div className="flex flex-wrap justify-between gap-1 py-2 border-b border-gray-100 text-[14px]">
                        <span className="text-hmuted">Category</span>
                        <span className="font-medium text-hsecondary">{product.categoryIds[0].name}</span>
                      </div>
                    )}
                    {selectedVariant && (
                      <>
                        {selectedVariant.sku && (
                          <div className="flex flex-wrap justify-between gap-1 py-2 border-b border-gray-100 text-[14px]">
                            <span className="text-hmuted">Variant SKU</span>
                            <span className="font-medium text-hsecondary">{selectedVariant.sku}</span>
                          </div>
                        )}
                        {product.showStock && (
                          <div className="flex flex-wrap justify-between gap-1 py-2 border-b border-gray-100 text-[14px]">
                            <span className="text-hmuted">Availability</span>
                            <span className="font-medium text-hsecondary">
                              {selectedVariant.inventoryCount > 0 ? `${selectedVariant.inventoryCount} in stock` : 'Out of stock'}
                            </span>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* Reviews are temporarily hidden.
                {activeTab === 'reviews' && (
                  <ProductReviewsSection productId={productId} />
                )}
                */}
              </div>
            </div>


            {/* Related Products */}
            {(isLoadingRelated || relatedProducts.length > 0) && (
              <div>
                <h2 className="font-semibold text-hsecondary mb-6 sm:mb-8 text-xl sm:text-2xl">
                  You May Also Like
                </h2>
                  <div className="flex flex-wrap justify-center gap-4">
                  {isLoadingRelated
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)] xl:w-[calc(20%-13px)]">
                          <ProductCardSkeleton />
                        </div>
                      ))
                    : relatedProducts.map(relatedProduct => (
                      <div key={relatedProduct._id} className="w-[calc(50%-8px)] md:w-[calc(33.333%-11px)] lg:w-[calc(25%-12px)] xl:w-[calc(20%-13px)]">
                        <ProductCard
                          product={relatedProduct}
                          onClickProduct={(item) => {
                            router.push(buildPath(`/products/${item._id || item.id}`));
                          }}
                        />
                      </div>
                    ))
                  }
                </div>
              </div>
            )}

          </div>
        </div>
      </section>
    </div>
  );
}
